import http from "@web-app/utils/http";
import { getBrowserTimezone } from "@web-app/utils/time";

export interface ChatMessageAttachment {
  audioDurationMs?: number;
  audioWaveform?: number[];
  fileName: string;
  fileSize: number;
  fileType: string;
  url: string;
}

export type ChatMessageType = "CALL" | "SERVICE_ALERT" | "TEXT";
export type CallStatus = "ENDED" | "ONGOING";

export interface CallParticipant {
  image: string;
  joinedAt: string;
  name: string;
  userId: string;
}

export type MessageSendingStatus = "failed" | "sending" | "sent";

export type ChatMessageActionType = "DISMISS" | "ESCALATE_TO_SUPPORT";

export interface ChatMessageAction {
  label: string;
  type: ChatMessageActionType;
}

export interface ChatMessage {
  attachments: ChatMessageAttachment[];
  callDuration?: number;
  callEndedAt?: string;
  callParticipants?: CallParticipant[];
  callStartedAt?: string;
  callStatus?: CallStatus;
  content: string;
  conversationId: string;
  createdAt: string;
  id: string;
  isRead: boolean;
  messageType?: ChatMessageType;
  readAt: string | null;
  senderId: string;
  senderIsDriver: boolean;
  sendingStatus?: MessageSendingStatus;
  suggestedActions?: ChatMessageAction[];
  updatedAt: string;
}

export interface ChatParticipant {
  role: "AI_AGENT" | "DRIVER" | "STAFF" | "SUPPORT_AGENT";
  userId: string;
}

export interface ProviderState {
  closedAt?: string;
  conversationSid?: string;
  escalatedAt?: string;
  previousType?: string;
  status?: "CLOSED" | "ESCALATED" | "PENDING";
  taskSid?: string;
}

export interface ChatConversation {
  createdAt: string;
  id: string;
  lastMessageAt: string;
  orderAppAvatarUrl: string;
  orderDisplayId: string;
  orderExternalId: string;
  orderId: string | null;
  participants: ChatParticipant[];
  providerState?: ProviderState;
  type?: string;
  updatedAt: string;
  workspaceId: string;
}

export interface ParticipantWithDetails {
  hasReadLast: boolean;
  image: string;
  isDisabled?: boolean;
  name: string;
  phone: string;
  role: "AI_AGENT" | "DRIVER" | "STAFF" | "SUPPORT_AGENT";
  userId: string;
}

export interface ConversationWithDetails {
  conversation: ChatConversation;
  lastMessage: ChatMessage | null;
  orderAppAvatarUrl: string;
  orderDisplayId: string;
  orderExternalId: string;
  participantsWithDetails: ParticipantWithDetails[];
  unreadCount: number;
}

export interface CreateConversationPayload {
  orderId?: string;
  type?: string;
  userIds?: string[];
  withAI?: boolean;
}

export interface SendMessagePayload {
  attachments: ChatMessageAttachment[];
  content: string;
  conversationId: string;
}

export const findOrCreateConversation = (
  payload: CreateConversationPayload,
) => {
  return http
    .post<ConversationWithDetails>("/chat/conversations", payload)
    .then((response) => response.data);
};

export const getConversations = (limit?: number, offset?: number) => {
  const params = new URLSearchParams();
  if (limit) params.append("limit", limit.toString());
  if (offset) params.append("offset", offset.toString());

  const queryString = params.toString();
  const url = `/chat/conversations${queryString ? `?${queryString}` : ""}`;

  return http
    .get<ConversationWithDetails[]>(url)
    .then((response) => response.data);
};

export const getConversation = (conversationId: string) => {
  return http
    .get<ConversationWithDetails>(`/chat/conversations/${conversationId}`)
    .then((response) => response.data);
};

export const getConversationByOrderId = (orderId: string) => {
  return http
    .get<ConversationWithDetails>(`/orders/${orderId}/conversation`)
    .then((response) => response.data);
};

export const getConversationMessages = (
  conversationId: string,
  limit?: number,
  offset?: number,
) => {
  const params = new URLSearchParams();
  if (limit) params.append("limit", limit.toString());
  if (offset) params.append("offset", offset.toString());

  const queryString = params.toString();
  const url = `/chat/conversations/${conversationId}/messages${queryString ? `?${queryString}` : ""}`;

  return http.get<ChatMessage[]>(url).then((response) => response.data);
};

export const sendMessage = (payload: SendMessagePayload) => {
  return http
    .post<ChatMessage>("/chat/messages", {
      ...payload,
      timezone: getBrowserTimezone(),
    })
    .then((response) => response.data);
};

export const markMessagesAsRead = (conversationId: string) => {
  return http
    .post(`/chat/conversations/${conversationId}/read`, {})
    .then((response) => response.data);
};

export const escalateConversation = (conversationId: string) => {
  return http
    .post(`/chat/conversations/${conversationId}/escalate`, {})
    .then((response) => response.data);
};

export const deescalateConversation = (conversationId: string) => {
  return http
    .post(`/chat/conversations/${conversationId}/deescalate`, {})
    .then((response) => response.data);
};
